import { create } from 'zustand';
import apiClient from '../lib/api';

export type SprintStatus = 'planning' | 'active' | 'completed' | 'cancelled';

export interface Sprint {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  status: SprintStatus;
  startDate: string;
  endDate: string;
  goal?: string;
  createdAt: string;
  updatedAt: string;
}

function normalizeSprint(raw: any): Sprint {
  return {
    id: raw?.id ?? '',
    tenantId: raw?.tenant_id ?? raw?.tenantId ?? '',
    projectId: raw?.project_id ?? raw?.projectId ?? '',
    name: raw?.name ?? '',
    status: (raw?.status ?? 'planning') as SprintStatus,
    startDate: raw?.start_date ?? raw?.startDate ?? '',
    endDate: raw?.end_date ?? raw?.endDate ?? '',
    goal: raw?.goal ?? undefined,
    createdAt: raw?.created_at ?? raw?.createdAt ?? '',
    updatedAt: raw?.updated_at ?? raw?.updatedAt ?? '',
  };
}

interface SprintState {
  sprints: Sprint[];
  isLoading: boolean;
  error: string | null;
  fetchSprints: (filters?: { projectId?: string; status?: string }) => Promise<void>;
  createSprint: (data: Partial<Sprint>) => Promise<void>;
  updateSprint: (id: string, data: Partial<Sprint>) => Promise<void>;
  deleteSprint: (id: string) => Promise<void>;
}

export const useSprintStore = create<SprintState>((set) => ({
  sprints: [],
  isLoading: false,
  error: null,

  fetchSprints: async (filters) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get('/work/sprints', { params: { limit: 100, ...filters } });
      const data = response.data?.data ?? [];
      set({ sprints: data.map(normalizeSprint), isLoading: false });
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        set({ sprints: [], isLoading: false, error: 'Please login to load sprints.' });
        return;
      }
      set({ sprints: [], isLoading: false, error: null });
    }
  },

  createSprint: async (data) => {
    const response = await apiClient.post('/work/sprints', data);
    const created = normalizeSprint(response.data);
    set((state) => ({ sprints: [created, ...state.sprints] }));
  },

  updateSprint: async (id, data) => {
    const response = await apiClient.patch(`/work/sprints/${id}`, data);
    const updated = normalizeSprint(response.data);
    set((state) => ({ sprints: state.sprints.map((s) => (s.id === id ? updated : s)) }));
  },

  deleteSprint: async (id) => {
    await apiClient.delete(`/work/sprints/${id}`);
    set((state) => ({ sprints: state.sprints.filter((s) => s.id !== id) }));
  },
}));
